import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Navigation, User, MessageSquare, Check, XCircle, Send, CheckCircle2, Clock, MapPin, Wrench, Users, ShieldCheck, Plus, FileText, Banknote, Upload, CreditCard, Receipt, Languages, Sparkles, Image as ImageIcon, Trash2, CheckCircle, ExternalLink, ChevronRight, Map as MapIcon, Calendar as CalendarIcon, Info, Zap, Phone, MessageCircle, MoreVertical, ChevronDown, CheckSquare, Download, Star, Tag, Loader2, PlayCircle, Target, MessageSquarePlus } from 'lucide-react';
import { RepairJob, UserRole, JobStatus, Staff, WorkTask, Invoice, ChatMessage, Service, Rating, Discount, Shop, Language } from '../types';
import { formatRiel } from '../constants';
import { translateText, generateInvoice } from '../services/geminiService';
import { ShopRating } from './ShopRating';
import { useTranslation } from '../LanguageContext';

interface BookingDetailPageProps {
  job: RepairJob;
  shop?: Shop;
  currentUserId: string;
  currentUserName: string;
  currentUserRole: UserRole;
  allStaff: Staff[];
  allServices: Service[];
  allRatings: Rating[];
  allDiscounts: Discount[];
  allJobs: RepairJob[];
  onStatusUpdate: (jobId: string, status: JobStatus) => void;
  onFinalizeSchedule: (jobId: string, slot: string) => void;
  onAssignStaff: (jobId: string, staffIds: string[]) => void;
  onSendMessage: (jobId: string, text: string) => void;
  onAddTask: (jobId: string, task: WorkTask) => void;
  onCreateInvoice: (jobId: string, invoice: Invoice) => void;
  onUploadProof: (jobId: string, imageUrl: string) => void;
  onVerifyPayment: (jobId: string) => void;
  onAddRating: (jobId: string, shopId: string, stars: number, comment: string) => void;
  onBack: () => void;
}

export const BookingDetailPage: React.FC<BookingDetailPageProps> = ({
  job,
  shop,
  currentUserId,
  currentUserName,
  currentUserRole,
  allStaff,
  allServices,
  allRatings,
  allDiscounts,
  allJobs,
  onStatusUpdate,
  onFinalizeSchedule,
  onAssignStaff,
  onSendMessage,
  onAddTask,
  onCreateInvoice,
  onUploadProof,
  onVerifyPayment,
  onAddRating,
  onBack
}) => {
  const { language } = useTranslation();
  const [activeTab, setActiveTab] = useState<'CHAT' | 'ADMIN' | 'FIELD' | 'BILLING'>('CHAT');
  const [newMessage, setNewMessage] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [isTranslating, setIsTranslating] = useState<string | null>(null);
  
  const [customTaskDesc, setCustomTaskDesc] = useState('');
  const [customTaskCost, setCustomTaskCost] = useState('');
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<string>('');
  const [showPackageTasks, setShowPackageTasks] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isUpdatingStaff, setIsUpdatingStaff] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const isShopOwner = currentUserRole === UserRole.SHOP_OWNER;
  const isStaff = currentUserRole === UserRole.STAFF;
  const isCustomer = currentUserRole === UserRole.CUSTOMER;
  const isShopSide = isShopOwner || isStaff;

  // Filter assigned staff for task attribution
  const assignedStaff = allStaff.filter(s => (job.assignedStaffIds || []).includes(s.id));

  // ... (texts object same as before, simplified for brevity in this replace block, assuming imports are fine)
  const texts: Record<string, Record<Language, string>> = {
    // ... texts mapping ...
    workbench: { EN: 'Workbench', KH: 'កន្លែងធ្វើការ', ZH: '工作台', JA: 'ワークベンチ', KO: '작업대' },
    case: { EN: 'Case', KH: 'លេខករណី', ZH: '案例', JA: 'ケース', KO: '사건' },
    details: { EN: 'View Details', KH: 'មើលព័ត៌មានលម្អិត', ZH: '查看详情', JA: '詳細を見る', KO: '상세 보기' },
    hide: { EN: 'Hide Details', KH: 'លាក់ព័ត៌មានលម្អិត', ZH: '隐藏详情', JA: '詳細を隠す', KO: '상세 숨기기' },
    decline: { EN: 'Decline', KH: 'បដិសេធ', ZH: '拒绝', JA: '辞退', KO: '거절' },
    accept: { EN: 'Accept & Schedule', KH: 'យល់ព្រម និងកំណត់ពេល', ZH: '接受并安排', JA: '承認とスケジューリング', KO: '수락 및 일정 예약' },
    map: { EN: 'View Map Link', KH: 'មើលព័ត៌មានផែនទី', ZH: '查看地图链接', JA: '地図を表示', KO: '지도 보기' },
    issue: { EN: 'Issue Overview', KH: 'សេចក្តីសង្ខេបបញ្ហា', ZH: '问题概览', JA: '問題の概要', KO: '이슈 개요' },
    appointment: { EN: 'Planned Appointment', KH: 'ការណាត់ជួបដែលបានគ្រោងទុក', ZH: '计划预约', JA: '予定された訪問', KO: '예정된 예약' },
    awaiting: { EN: 'Awaiting confirmation', KH: 'រង់ចាំការបញ្ជាក់', ZH: '等待确认', JA: '確認待ち', KO: '확인 대기 중' },
    estimate_label: { EN: 'Market Estimate', KH: 'តម្លៃទីផ្សារប៉ាន់ស្មាន', ZH: '市场估价', JA: '市場見積', KO: '시장 견적' },
    feed: { EN: 'Feed', KH: 'សារ', ZH: '动态', JA: 'フィード', KO: '피드' },
    team: { EN: 'Manage', KH: 'គ្រប់គ្រង', ZH: '管理', JA: '管理', KO: '관리' },
    tasks: { EN: 'Tasks', KH: 'ការងារ', ZH: '任务', JA: 'タスク', KO: '작업' },
    invoice: { EN: 'Invoice', KH: 'វិក្កយបត្រ', ZH: '发票', JA: '請求書', KO: '인보이스' },
    translate: { EN: 'TRANSLATE', KH: 'បកប្រែ', ZH: '翻译', JA: '翻訳', KO: '번역' },
    slot: { EN: 'Select Final Slot', KH: 'ជ្រើសរើសពេលណាត់ជួបចុងក្រោយ', ZH: '选择最终时间段', JA: '最終スロットを選択', KO: '최종 슬롯 선택' },
    assign: { EN: 'Assign Team', KH: 'ចាត់តាំងក្រុមការងារ', ZH: '分配团队', JA: 'チームを割り当て', KO: '팀 배정' },
    work_tasks: { EN: 'Work Tasks', KH: 'សកម្មភាពការងារ', ZH: '工作任务', JA: '作業タスク', KO: '작업 항목' },
    start: { EN: 'Start Repair', KH: 'ចាប់ផ្តើមជួសជុល', ZH: '开始维修', JA: '修理開始', KO: '수리 시작' },
    load_pkg: { EN: 'Load Service Packages', KH: 'ប្រើកញ្ចប់សេវាកម្ម', ZH: '加载服务包', JA: 'サービスパッケージを読み込む', KO: '서비스 패키지 불러오기' },
    manual_entry: { EN: 'Log Manual Entry', KH: 'កត់ត្រាការងារដោយផ្ទាល់', ZH: '手动录入日志', JA: '手動エントリを記録', KO: '수동 항목 기록' },
    log_btn: { EN: 'Log Task', KH: 'កត់ត្រាការងារ', ZH: '记录任务', JA: 'タスクを記録', KO: '작업 기록' },
    billing: { EN: 'Customer Billing', KH: 'ការទូទាត់ប្រាក់អតិថិជន', ZH: '客户计费', JA: '顧客への請求', KO: '고객 청구' },
    ai_inv: { EN: 'Generate AI Invoice', KH: 'បង្កើតវិក្កយបត្រដោយ AI', ZH: '生成 AI 发票', JA: 'AI 請求書を生成', KO: 'AI 인보이스 생성' },
    settle: { EN: 'Settle Payment', KH: 'បង់ប្រាក់', ZH: '结清付款', JA: '支払いを完了する', KO: '결제하기' },
    verify: { EN: 'Verify Settlement', KH: 'ផ្ទៀងផ្ទាត់ការបង់ប្រាក់', ZH: '核实结清', JA: '支払いを確認', KO: '결제 확인' },
    thank_you: { EN: 'Thank you for your feedback!', KH: 'សូមអរគុណសម្រាប់ការផ្តល់មតិ!', ZH: '感谢您的反馈！', JA: 'フィードバックありがとうございます！', KO: '피드백해 주셔서 감사합니다!' },
    work_desc: { EN: 'Work Description', KH: 'ការពិពណ៌នាការងារ', ZH: '工作描述', JA: '作業内容', KO: '작업 설명' },
    amount_label: { EN: 'Amount (៛)', KH: 'ចំនួនទឹកប្រាក់ (៛)', ZH: '金额 (៛)', JA: '金額 (៛)', KO: '금액 (៛)' },
    ref: { EN: 'Reference', KH: 'យោង', ZH: '引用', JA: '参照', KO: '참조' },
    subtotal: { EN: 'Subtotal', KH: 'សរុបបណ្តោះអាសន្ន', ZH: '小计', JA: '小計', KO: '소계' },
    grand_total: { EN: 'Grand Total', KH: 'សរុបចុងក្រោយ', ZH: '总计', JA: '合計', KO: '총계' },
    waiting_logs: { EN: 'Waiting for logs', KH: 'រង់ចាំការកត់ត្រាការងារ', ZH: '等待日志', JA: 'ログ待ち', KO: '로그 대기 중' },
    manual_ai_req: { EN: 'Manual or AI breakdown required', KH: 'តម្រូវឱ្យបញ្ចូលដោយដៃ ឬដោយ AI', ZH: '需要手动或 AI 分解', JA: '手動または AI による内訳が必要', KO: '수리 내역 필요' },
    payment_status: { EN: 'Submission Status', KH: 'ស្ថានភាពការដាក់ស្នើ', ZH: '提交状态', JA: '提出状況', KO: '제출 상태' },
    discount_applied: { EN: 'Discount Applied', KH: 'ការបញ្ចុះតម្លៃត្រូវបានអនុវត្ត', ZH: '已应用折扣', JA: '割引適用済み', KO: '할인 적용됨' },
    vat: { EN: 'VAT', KH: 'អាករ (VAT)', ZH: '增值税', JA: '消費税', KO: '부가세' },
    select_receipt: { EN: 'Select Digital Receipt', KH: 'ជ្រើសរើសវិក្កយបត្រឌីជីថល', ZH: '选择电子收据', JA: 'デジタル領収書を選択', KO: '디지털 영수증 선택' },
    transfer_msg: { EN: 'Transfer and upload receipt for verification.', KH: 'ផ្ទេរប្រាក់ និងផ្ទុកឡើងវិក្កយបត្រដើម្បីផ្ទៀងផ្ទាត់។', ZH: '转账并上传收据进行验证。', JA: '振込を行い、確認のために領収書をアップロードしてください。', KO: '이체 후 확인을 위해 영수증을 업로드하십시오.' },
    finish_repair: { EN: 'Finish Repair Session', KH: 'បញ្ចប់ការជួសជុល', ZH: '完成维修会话', JA: '修理セッションを終了', KO: '수리 세션 종료' },
    tech_placeholder: { EN: 'Choose Technician', KH: 'ជ្រើសរើសជាង', ZH: '选择技师', JA: '技術者を選択', KO: '기술자 선택' },
    no_messages: { EN: 'No messages yet. Start a conversation!', KH: 'មិនទាន់មានសារនៅឡើយទេ។ ចាប់ផ្តើមការសន្ទនា!', ZH: '尚无消息。开始对话吧！', JA: 'メッセージはありません。会話を始めましょう！', KO: '메시지가 없습니다. 대화를 시작하세요!' }
  };

  const shopRatings = allRatings.filter(r => r.shopId === job.shopId);

  useEffect(() => {
    if (activeTab === 'CHAT') chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [job.messages, activeTab]);

  const handleSendChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    onSendMessage(job.id, newMessage);
    setNewMessage('');
  };

  const handleTranslate = async (msg: ChatMessage) => {
    if (msg.translatedText) return;
    setIsTranslating(msg.id);
    try {
      const hasKhmer = /[\u1780-\u17FF]/.test(msg.text);
      const target = hasKhmer ? 'EN' : 'KH';
      const result = await translateText(msg.text, target);
      msg.translatedText = result;
    } catch (e) { console.error(e); }
    setIsTranslating(null);
  };

  const handleGenerateAiInvoice = async () => {
    if ((job.workTasks || []).length === 0) return;
    setIsAiGenerating(true);
    try {
      const tasksText = job.workTasks.map(t => `${t.description} (Cost: ${t.cost}៛)`).join(', ');
      const items = await generateInvoice(tasksText);
      
      const subtotal = items.reduce((acc: number, cur: any) => acc + cur.amount, 0);
      
      const vatPercentage = shop?.vatPercentage || 0;
      const discountAmt = 0; 
      const taxableAmount = Math.max(0, subtotal - discountAmt);
      const vatAmt = taxableAmount * (vatPercentage / 100);
      const total = taxableAmount + vatAmt;

      onCreateInvoice(job.id, {
        id: `INV-${Date.now().toString().slice(-6)}`,
        items,
        subtotal,
        discountAmount: discountAmt,
        vatAmount: vatAmt,
        totalAmount: total,
        status: 'UNPAID',
        createdAt: new Date().toISOString()
      });
    } catch (e) { console.error(e); }
    setIsAiGenerating(false);
  };

  const addWorkTask = (desc: string, cost: number, fromPackage: boolean) => {
    let staffName = currentUserName;
    let staffId = currentUserId;

    if (selectedTechnicianId) {
      const selected = allStaff.find(s => s.id === selectedTechnicianId);
      if (selected) {
        staffName = selected.name;
        staffId = selected.id;
      }
    }

    onAddTask(job.id, {
      id: `task-${Date.now()}`,
      description: desc,
      cost,
      isFromPackage: fromPackage,
      loggedAt: new Date().toISOString(),
      staffName: staffName,
      staffId: staffId
    });
  };

  const getStatusConfig = (status: JobStatus) => {
    const configs: Record<string, { label: string, color: string, bg: string, icon: any }> = {
      [JobStatus.PENDING]: { label: 'Awaiting', color: 'text-amber-500', bg: 'bg-amber-50', icon: Clock },
      [JobStatus.CONFIRMED]: { label: 'Scheduled', color: 'text-blue-500', bg: 'bg-blue-50', icon: CalendarIcon },
      [JobStatus.IN_PROGRESS]: { label: 'Fixing', color: 'text-violet-500', bg: 'bg-violet-50', icon: Zap },
      [JobStatus.COMPLETED]: { label: 'Finished', color: 'text-emerald-500', bg: 'bg-emerald-50', icon: CheckCircle },
      [JobStatus.REJECTED]: { label: 'Rejected', color: 'text-red-500', bg: 'bg-red-50', icon: XCircle },
    };
    return configs[status] || { label: status, color: 'text-slate-500', bg: 'bg-slate-50', icon: Info };
  };

  const handleOpenMap = () => {
    const loc = job.customerLocation;
    if (loc.mapUrl) { window.open(loc.mapUrl, '_blank'); }
    else if (loc.lat !== undefined && loc.lng !== undefined) { window.open(`https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`, '_blank'); }
  };

  const handleStaffAssign = async (staffId: string) => {
    setIsUpdatingStaff(staffId);
    const currentAssignments = job.assignedStaffIds || [];
    const isAssigned = currentAssignments.includes(staffId);
    const next = isAssigned 
      ? currentAssignments.filter(id => id !== staffId) 
      : [...currentAssignments, staffId];
    
    try {
      await onAssignStaff(job.id, next);
    } finally {
      setIsUpdatingStaff(null);
    }
  };

  const status = getStatusConfig(job.status);

  return (
    <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-7xl mx-auto flex flex-col min-h-screen animate-in fade-in duration-500 overflow-x-hidden">
      {/* ... previous JSX content ... */}
      
      {/* Tab Content Section */}
      <div className="flex flex-col lg:flex-row gap-8 flex-1">
        {/* ... Info Sidebar ... */}
        
        <div className="w-full lg:w-[70%] flex flex-col bg-white rounded-[3rem] md:rounded-[4rem] shadow-2xl shadow-slate-200/50 border border-slate-50 overflow-hidden relative">
           
           {/* Tabs */}
           <div className="sticky top-0 z-30 flex p-3 bg-white/95 backdrop-blur-md border-b border-slate-50 gap-2">
             <button onClick={() => setActiveTab('CHAT')} className={`flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'CHAT' ? 'bg-slate-900 shadow-xl text-white' : 'text-slate-400 hover:bg-slate-50'}`}>
                <MessageSquare size={16} /> {texts.feed[language]}
             </button>
             {isShopSide && <button onClick={() => setActiveTab('ADMIN')} className={`flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'ADMIN' ? 'bg-slate-900 shadow-xl text-white' : 'text-slate-400 hover:bg-slate-50'}`}>
                <Users size={16} /> {texts.team[language]}
             </button>}
             {isShopSide && <button onClick={() => setActiveTab('FIELD')} className={`flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'FIELD' ? 'bg-slate-900 shadow-xl text-white' : 'text-slate-400 hover:bg-slate-50'}`}>
                <CheckSquare size={16} /> {texts.tasks[language]}
             </button>}
             <button onClick={() => setActiveTab('BILLING')} className={`flex-1 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'BILLING' ? 'bg-slate-900 shadow-xl text-white' : 'text-slate-400 hover:bg-slate-50'}`}>
                <Banknote size={16} /> {texts.invoice[language]}
             </button>
           </div>

           <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-slate-50/10 min-h-[400px]">
              {/* CHAT TAB content ... */}
              {activeTab === 'CHAT' && (
                <div className="h-full flex flex-col">
                   {/* ... Chat implementation ... */}
                   <div className="flex-1 space-y-10 pb-6">
                      {(job.messages || []).length === 0 ? (
                        <div className="py-24 text-center opacity-30 flex flex-col items-center animate-in fade-in">
                           <MessageSquarePlus size={48} className="mb-4 text-slate-400" />
                           <p className="text-lg font-black text-slate-500 uppercase tracking-widest">{texts.no_messages[language]}</p>
                        </div>
                      ) : (
                        (job.messages || []).map(msg => (
                          <div key={msg.id} className={`flex ${msg.senderId === currentUserId ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-4`}>
                             <div className={`max-w-[85%] md:max-w-[75%] space-y-2 ${msg.senderId === currentUserId ? 'items-end' : 'items-start'}`}>
                                <div className="flex items-center gap-2 px-3">
                                   <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">{msg.senderName}</span>
                                </div>
                                <div className={`p-5 md:p-7 rounded-[2rem] shadow-sm relative group ${
                                  msg.senderId === currentUserId
                                    ? 'bg-slate-900 text-white rounded-tr-none shadow-xl shadow-slate-200' 
                                    : 'bg-white text-slate-700 border border-slate-100 rounded-tl-none shadow-sm'
                                }`}>
                                   <p className="font-bold text-sm md:text-base leading-relaxed">{msg.text}</p>
                                   <button onClick={() => handleTranslate(msg)} className={`absolute -bottom-6 ${msg.senderId === currentUserId ? 'right-0' : 'left-0'} opacity-0 group-hover:opacity-100 transition-opacity text-[8px] font-black text-violet-600 flex items-center gap-1.5 py-1.5 px-3 bg-white rounded-full shadow-lg border border-slate-100 z-10`}>
                                      {isTranslating === msg.id ? <div className="w-3 h-3 border border-violet-600 border-t-transparent rounded-full animate-spin" /> : <Languages size={12} />}
                                      {texts.translate[language]}
                                   </button>
                                </div>
                                <p className="text-[8px] font-black text-slate-300 px-4">{new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                             </div>
                          </div>
                        ))
                      )}
                      <div ref={chatEndRef} />
                   </div>
                   <form onSubmit={handleSendChat} className="sticky bottom-0 relative animate-in slide-in-from-bottom-8">
                      <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="..." className="w-full p-6 md:p-8 rounded-[2.5rem] bg-white border border-slate-100 shadow-2xl pr-24 md:pr-32 font-bold text-sm md:text-lg outline-none focus:ring-4 focus:ring-violet-100" />
                      <button type="submit" className="absolute right-3 top-3 bottom-3 aspect-square bg-slate-900 text-white rounded-[1.8rem] flex items-center justify-center hover:bg-violet-600 transition-all transform active:scale-95"><Send size={24}/></button>
                   </form>
                </div>
              )}

              {/* ADMIN TAB */}
              {activeTab === 'ADMIN' && isShopSide && (
                 <div className="space-y-12 animate-in slide-in-from-bottom-4">
                    {/* ... Schedule Section ... */}
                    
                    <section className="bg-white p-8 md:p-12 rounded-[3rem] border border-slate-100 shadow-sm">
                       <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-10 flex items-center gap-4"><Users size={20} className="text-violet-500" /> {texts.assign[language]}</h4>
                       <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                          {allStaff.length === 0 ? (
                            <div className="col-span-full py-12 text-center text-slate-300 font-bold italic">No team members found. Add them in Dashboard &gt; Team.</div>
                          ) : (
                            allStaff.map(s => {
                               const isAssigned = (job.assignedStaffIds || []).includes(s.id);
                               const isLoading = isUpdatingStaff === s.id;
                               return (
                                 <button key={s.id} disabled={!!isUpdatingStaff} onClick={() => handleStaffAssign(s.id)} className={`p-5 rounded-[2.5rem] border-2 transition-all flex flex-col items-center gap-4 transform active:scale-95 ${isAssigned ? 'bg-violet-50 border-violet-500 shadow-xl shadow-violet-100' : 'bg-slate-50 border-transparent hover:border-slate-200'} ${isLoading ? 'opacity-50' : ''}`}>
                                    <div className="relative">
                                      <img src={s.avatarUrl} className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover shadow-sm" />
                                      {isLoading && <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-2xl"><Loader2 size={24} className="animate-spin text-violet-600" /></div>}
                                    </div>
                                    <span className="text-[10px] font-black text-slate-900 uppercase tracking-tighter truncate w-full text-center">{s.name}</span>
                                 </button>
                               );
                            })
                          )}
                       </div>
                    </section>
                 </div>
              )}

              {/* FIELD and BILLING tabs ... */}
              {activeTab === 'FIELD' && isShopSide && (
                <div className="space-y-8 animate-in slide-in-from-bottom-4">
                   {/* ... Field Tasks UI ... */}
                   {/* ... Simplified for output ... */}
                </div>
              )}

              {activeTab === 'BILLING' && (
                 <div className="space-y-10 animate-in slide-in-from-bottom-4">
                    {/* ... Billing UI ... */}
                    {/* ... Simplified for output ... */}
                 </div>
              )}
           </div>
        </div>
      </div>
    </div>
  );
};